import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";

import SendIcon from "@mui/icons-material/Send";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import KeyIcon from "@mui/icons-material/Key";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ShieldIcon from "@mui/icons-material/Shield";
import BoltIcon from "@mui/icons-material/Bolt";
import TagIcon from "@mui/icons-material/Tag";

import { PrismLight as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import bashLang from "react-syntax-highlighter/dist/esm/languages/prism/bash";
import javascriptLang from "react-syntax-highlighter/dist/esm/languages/prism/javascript";
import pythonLang from "react-syntax-highlighter/dist/esm/languages/prism/python";
import phpLang from "react-syntax-highlighter/dist/esm/languages/prism/php";
import jsonLang from "react-syntax-highlighter/dist/esm/languages/prism/json";

SyntaxHighlighter.registerLanguage("bash", bashLang);
SyntaxHighlighter.registerLanguage("javascript", javascriptLang);
SyntaxHighlighter.registerLanguage("python", pythonLang);
SyntaxHighlighter.registerLanguage("php", phpLang);
SyntaxHighlighter.registerLanguage("json", jsonLang);

import api from "../../services/api";

const STORAGE_KEY = "messages_api_history";
const MAX_HISTORY = 15;
const MAX_FILE_SIZE = 16 * 1024 * 1024;

const SELECT_MENU_PROPS = {
  PaperProps: {
    sx: {
      maxHeight: 280,
      mt: 0.5,
      borderRadius: 1.5,
      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
      "& .MuiMenuItem-root": {
        fontSize: 13,
        minHeight: 32,
        py: 0.5,
      },
    },
  },
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: "top", horizontal: "left" },
};

const TICKET_STATUSES = [
  { value: "", label: "Sem ticket" },
  { value: "pending", label: "Pendente" },
  { value: "open", label: "Aberto" },
  { value: "closed", label: "Fechado" },
  { value: "archived", label: "Arquivado" },
];

const MESSAGE_TEMPLATES = [
  {
    name: "Boas-vindas",
    body: "Olá! Seja bem-vindo(a) ao nosso atendimento. Como posso ajudar?",
  },
  {
    name: "Confirmação",
    body: "Seu pedido foi confirmado com sucesso. Em breve enviaremos mais detalhes.",
  },
  {
    name: "Suporte",
    body: "Olá! Recebemos sua solicitação. Um especialista entrará em contato em breve.",
  },
];

const useStyles = makeStyles()((theme) => {
  const isDark = theme.palette.mode === "dark";
  return {
    root: {
      minHeight: "100vh",
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(6),
      backgroundColor: isDark ? "#0b0d12" : "#f4f6fb",
    },
    header: {
      marginBottom: theme.spacing(3),
    },
    headerTitle: {
      fontWeight: 700,
      letterSpacing: -0.5,
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.5),
    },
    headerSubtitle: {
      color: theme.palette.text.secondary,
      marginTop: theme.spacing(0.5),
    },
    panel: {
      borderRadius: 12,
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.paper,
      overflow: "hidden",
    },
    panelHeader: {
      padding: theme.spacing(1, 1.75),
      borderBottom: `1px solid ${theme.palette.divider}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(1),
      backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)",
      minHeight: 42,
    },
    panelTitle: {
      fontWeight: 600,
      fontSize: 12,
      textTransform: "uppercase",
      letterSpacing: 0.6,
      color: theme.palette.text.secondary,
    },
    panelBody: {
      padding: theme.spacing(2),
    },
    endpointBar: {
      display: "flex",
      alignItems: "stretch",
      borderRadius: 8,
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: isDark ? "#11141b" : "#ffffff",
      overflow: "hidden",
      height: 42,
    },
    methodBadge: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: theme.spacing(0, 1.75),
      backgroundColor: "#f59e0b",
      color: "#fff",
      fontWeight: 700,
      fontSize: 12,
      letterSpacing: 0.5,
      minWidth: 64,
    },
    endpointUrl: {
      flex: 1,
      padding: theme.spacing(0, 1.5),
      fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, monospace',
      fontSize: 12.5,
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      color: theme.palette.text.primary,
      display: "flex",
      alignItems: "center",
    },
    endpointToken: {
      color: theme.palette.primary.main,
      fontWeight: 600,
    },
    sendButton: {
      borderRadius: 0,
      paddingLeft: theme.spacing(2.5),
      paddingRight: theme.spacing(2.5),
      fontWeight: 600,
      textTransform: "none",
      fontSize: 13,
      minWidth: 110,
      height: "100%",
      boxShadow: "none",
    },
    sectionLabel: {
      fontSize: 11,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: 0.7,
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(0.75),
    },
    fieldRow: {
      marginBottom: theme.spacing(1.5),
    },
    fileDrop: {
      border: `1.5px dashed ${theme.palette.divider}`,
      borderRadius: 8,
      padding: theme.spacing(2.5, 2),
      textAlign: "center",
      cursor: "pointer",
      transition: "all 160ms ease",
      "&:hover": {
        borderColor: theme.palette.primary.main,
        backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)",
      },
    },
    fileDropActive: {
      borderColor: theme.palette.primary.main,
      backgroundColor: isDark ? "rgba(99,102,241,0.08)" : "rgba(99,102,241,0.06)",
    },
    filePill: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      padding: theme.spacing(1, 1.5),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 8,
      backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
    },
    codeBlock: {
      "& pre": {
        margin: 0,
        borderRadius: 8,
        maxHeight: 480,
      },
    },
    statusChip: {
      fontWeight: 700,
      fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, monospace',
    },
    metaBar: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(2),
      padding: theme.spacing(1.5, 2),
      borderBottom: `1px solid ${theme.palette.divider}`,
      backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)",
      flexWrap: "wrap",
    },
    metaItem: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(0.5),
      fontSize: 12,
      color: theme.palette.text.secondary,
    },
    metaValue: {
      fontWeight: 600,
      color: theme.palette.text.primary,
      fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, monospace',
    },
    emptyState: {
      padding: theme.spacing(6, 3),
      textAlign: "center",
      color: theme.palette.text.secondary,
    },
    historyItem: {
      padding: theme.spacing(0.875, 1.25),
      borderBottom: `1px solid ${theme.palette.divider}`,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      transition: "background-color 120ms ease",
      "&:hover": {
        backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)",
      },
      "&:last-child": { borderBottom: "none" },
    },
    historyItemActive: {
      backgroundColor: isDark ? "rgba(99,102,241,0.12)" : "rgba(99,102,241,0.08)",
    },
    historyMethod: {
      fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, monospace',
      fontSize: 10,
      fontWeight: 700,
      padding: theme.spacing(0.25, 0.75),
      borderRadius: 4,
      color: "#fff",
      backgroundColor: "#f59e0b",
    },
    languageTabs: {
      minHeight: 36,
      "& .MuiTab-root": {
        minHeight: 36,
        textTransform: "none",
        fontSize: 12,
        fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, monospace',
        padding: theme.spacing(0.5, 1.5),
        minWidth: "auto",
      },
    },
    connectionStatus: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(0.75),
      fontSize: 12,
      color: theme.palette.text.secondary,
    },
    connectionDot: {
      width: 8,
      height: 8,
      borderRadius: "50%",
    },
    quotaStrip: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(2),
      padding: theme.spacing(1, 1.5),
      marginBottom: theme.spacing(2),
      borderRadius: 8,
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: isDark ? "#11141b" : "#ffffff",
      flexWrap: "wrap",
    },
    quotaShield: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(0.75),
      paddingRight: theme.spacing(1.5),
      borderRight: `1px solid ${theme.palette.divider}`,
    },
    quotaItem: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      flex: 1,
      minWidth: 180,
    },
    quotaLabel: {
      fontSize: 11,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: 0.5,
      color: theme.palette.text.secondary,
      minWidth: 36,
    },
    quotaBarTrack: {
      flex: 1,
      height: 6,
      borderRadius: 3,
      backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)",
      overflow: "hidden",
    },
    quotaBarFill: {
      height: "100%",
      transition: "width 220ms ease, background-color 220ms ease",
    },
    quotaCount: {
      fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, monospace',
      fontSize: 12,
      fontWeight: 600,
      minWidth: 70,
      textAlign: "right",
    },
  };
});

const QuotaStrip = ({ rateInfo, classes }) => {
  // Sem rateInfo ainda: mostra explicação passiva pro usuário não estranhar
  // o jitter ao enviar a primeira mensagem.
  if (!rateInfo) {
    return (
      <Box className={classes.quotaStrip}>
        <Box className={classes.quotaShield}>
          <ShieldIcon fontSize="small" sx={{ color: "#10b981" }} />
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Anti-ban ativo</Typography>
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
        >
          <BoltIcon sx={{ fontSize: 14 }} />
          Envios espaçados (2-5s) e limitados por hora/dia para proteger a conexão do WhatsApp.
        </Typography>
      </Box>
    );
  }

  const { hourUsed = 0, hourLimit = 0, dayUsed = 0, dayLimit = 0 } = rateInfo;
  const hourPct = hourLimit > 0 ? Math.min(100, (hourUsed / hourLimit) * 100) : 0;
  const dayPct = dayLimit > 0 ? Math.min(100, (dayUsed / dayLimit) * 100) : 0;
  const colorFor = (pct) => {
    if (pct >= 100) return "#ef4444";
    if (pct >= 80) return "#f59e0b";
    return "#10b981";
  };

  return (
    <Box className={classes.quotaStrip}>
      <Box className={classes.quotaShield}>
        <ShieldIcon fontSize="small" sx={{ color: colorFor(Math.max(hourPct, dayPct)) }} />
        <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Anti-ban</Typography>
      </Box>
      <Box className={classes.quotaItem}>
        <Typography className={classes.quotaLabel}>Hora</Typography>
        <Box className={classes.quotaBarTrack}>
          <Box
            className={classes.quotaBarFill}
            sx={{ width: `${hourPct}%`, backgroundColor: colorFor(hourPct) }}
          />
        </Box>
        <Typography className={classes.quotaCount}>
          {hourUsed}/{hourLimit}
        </Typography>
      </Box>
      <Box className={classes.quotaItem}>
        <Typography className={classes.quotaLabel}>Dia</Typography>
        <Box className={classes.quotaBarTrack}>
          <Box
            className={classes.quotaBarFill}
            sx={{ width: `${dayPct}%`, backgroundColor: colorFor(dayPct) }}
          />
        </Box>
        <Typography className={classes.quotaCount}>
          {dayUsed}/{dayLimit}
        </Typography>
      </Box>
    </Box>
  );
};

const formatBytes = (bytes) => {
  if (!bytes) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(i === 0 ? 0 : 1)} ${sizes[i]}`;
};

const statusColor = (code) => {
  if (!code) return "default";
  if (code >= 200 && code < 300) return "success";
  if (code >= 300 && code < 400) return "info";
  if (code >= 400 && code < 500) return "warning";
  return "error";
};

const statusText = (code) => {
  const map = {
    200: "OK",
    201: "Created",
    204: "No Content",
    400: "Bad Request",
    401: "Unauthorized",
    403: "Forbidden",
    404: "Not Found",
    422: "Unprocessable",
    500: "Server Error",
  };
  return map[code] || "";
};

const MessagesAPI = () => {
  const { classes, cx } = useStyles();

  // Data
  const [connections, setConnections] = useState([]);
  const [users, setUsers] = useState([]);
  const [queues, setQueues] = useState([]);
  const [loadingConnections, setLoadingConnections] = useState(true);
  const [connectionsError, setConnectionsError] = useState(null);

  // Form
  const [connectionId, setConnectionId] = useState("");
  const [number, setNumber] = useState("");
  const [body, setBody] = useState("");
  const [file, setFile] = useState(null);
  const [saveContact, setSaveContact] = useState(false);
  const [createTicket, setCreateTicket] = useState(false);
  const [ticketStatus, setTicketStatus] = useState("");
  const [ticketUser, setTicketUser] = useState("");
  const [ticketQueue, setTicketQueue] = useState("");
  const [showToken, setShowToken] = useState(false);

  // UI state
  const [errors, setErrors] = useState({});
  const [requestTab, setRequestTab] = useState(0);
  const [responseTab, setResponseTab] = useState(0);
  const [codeLanguage, setCodeLanguage] = useState(0);
  const [sending, setSending] = useState(false);
  const [response, setResponse] = useState(null);
  const [rateInfo, setRateInfo] = useState(null); // { hourUsed, hourLimit, dayUsed, dayLimit, jitterAvgMs }
  const [history, setHistory] = useState([]);
  const [selectedHistoryId, setSelectedHistoryId] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [copyKey, setCopyKey] = useState(null);

  const fileInputRef = useRef(null);

  const backendUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

  const selectedConnection = useMemo(
    () => connections.find((c) => c.id === connectionId) || null,
    [connections, connectionId]
  );

  const apiToken = selectedConnection?.apiToken || "";

  const endpointUrl = useMemo(() => {
    return `${backendUrl}/api/messages/send/${apiToken || "{token}"}`;
  }, [backendUrl, apiToken]);

  const loadConnections = useCallback(async () => {
    setLoadingConnections(true);
    setConnectionsError(null);
    try {
      const { data } = await api.get("/api_connection/api_data");
      setConnections(Array.isArray(data?.whatsapps) ? data.whatsapps : []);
      setUsers(Array.isArray(data?.users) ? data.users : []);
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Não foi possível carregar as conexões.";
      setConnectionsError(msg);
      setConnections([]);
      setUsers([]);
    } finally {
      setLoadingConnections(false);
    }
  }, []);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) setHistory(JSON.parse(stored));
    } catch (_) {
      // ignore corrupt history
    }
  }, []);

  useEffect(() => {
    if (selectedConnection) {
      setQueues(Array.isArray(selectedConnection.queues) ? selectedConnection.queues : []);
      setTicketQueue("");
    } else {
      setQueues([]);
    }
  }, [selectedConnection]);

  const showSnackbar = (message, severity = "info") =>
    setSnackbar({ open: true, message, severity });

  const validate = useCallback(() => {
    const e = {};
    if (!connectionId) e.connection = "Selecione uma conexão";
    if (!number.trim()) {
      e.number = "Número é obrigatório";
    } else if (!/^\d{10,15}$/.test(number.replace(/\D/g, ""))) {
      e.number = "Use formato internacional, ex: 5511999999999";
    }
    if (!body.trim() && !file) e.body = "Informe uma mensagem ou anexo";
    if (createTicket) {
      if ((ticketStatus === "open" || ticketStatus === "archived") && !ticketUser) {
        e.ticketUser = "Usuário obrigatório para este status";
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }, [connectionId, number, body, file, createTicket, ticketStatus, ticketUser]);

  const buildRequestPayload = useCallback(
    () => ({
      contact: {
        number: number.replace(/\D/g, ""),
        create: saveContact,
      },
      message: {
        body,
        file: file ? file.name : null,
      },
      ticket: {
        create: createTicket,
        assigned: {
          status: createTicket ? ticketStatus || null : null,
          user: createTicket && ticketUser ? ticketUser : null,
          queue: createTicket && ticketQueue ? ticketQueue : null,
        },
      },
    }),
    [number, saveContact, body, file, createTicket, ticketStatus, ticketUser, ticketQueue]
  );

  const persistHistory = (entry) => {
    const next = [entry, ...history].slice(0, MAX_HISTORY);
    setHistory(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (_) {
      // storage may be full or unavailable
    }
  };

  // Extrai os campos extras que o backend devolve junto da resposta:
  // jobId pra rastreio, rate pras barras de cota, warnings pros chips.
  const extractMeta = (data) => ({
    jobId: data?.jobId,
    rate: data?.rate || null,
    warnings: Array.isArray(data?.warnings) ? data.warnings : [],
    warnContact: data?.warnContact,
    warnTicket: data?.warnTicket,
  });

  const handleSend = async () => {
    if (!validate()) {
      showSnackbar("Corrija os campos destacados.", "warning");
      return;
    }
    setSending(true);
    setResponse(null);
    setResponseTab(0);

    const payload = buildRequestPayload();
    const started = performance.now();

    const fd = new FormData();
    fd.append("data", JSON.stringify(payload));
    if (file) fd.append("file", file);

    let resp;
    try {
      resp = await api.post(`/api/messages/send/${apiToken}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const elapsed = Math.round(performance.now() - started);
      const sizeBytes = JSON.stringify(resp.data || {}).length;
      const meta = extractMeta(resp.data);
      if (meta.rate) setRateInfo(meta.rate);

      const result = {
        status: resp.status,
        statusText: resp.statusText,
        time: elapsed,
        size: sizeBytes,
        headers: resp.headers,
        data: resp.data,
        ok: resp.data?.ok !== false,
        meta,
      };
      setResponse(result);
      persistHistory({
        id: Date.now(),
        ts: new Date().toISOString(),
        endpoint: endpointUrl,
        connectionName: selectedConnection?.name || "",
        number: payload.contact.number,
        payload,
        result,
      });
      showSnackbar("Requisição enviada com sucesso.", result.ok ? "success" : "warning");
    } catch (err) {
      const elapsed = Math.round(performance.now() - started);
      const errStatus = err?.response?.status || 0;
      const errData = err?.response?.data || { error: err?.message || "Erro desconhecido" };
      const meta = extractMeta(errData);
      if (meta.rate) setRateInfo(meta.rate);

      // 429 = bateu na cota. Mostra como rate_limited, não como falha genérica.
      const isRateLimit = errStatus === 429 || errData?.status === "rate_limited";
      const result = {
        status: errStatus,
        statusText: err?.response?.statusText || "Network Error",
        time: elapsed,
        size: JSON.stringify(errData).length,
        headers: err?.response?.headers || {},
        data: errData,
        ok: false,
        meta,
        rateLimited: isRateLimit,
      };
      setResponse(result);
      persistHistory({
        id: Date.now(),
        ts: new Date().toISOString(),
        endpoint: endpointUrl,
        connectionName: selectedConnection?.name || "",
        number: payload.contact.number,
        payload,
        result,
      });
      const msg = isRateLimit
        ? (errData?.error || "Cota de envio atingida. Tente novamente mais tarde.")
        : (errData?.error || errData?.message || "Falha ao enviar requisição.");
      showSnackbar(msg, isRateLimit ? "warning" : "error");
    } finally {
      setSending(false);
    }
  };

  const handleFile = (f) => {
    if (!f) return;
    if (f.size > MAX_FILE_SIZE) {
      showSnackbar(`Arquivo excede o limite de ${formatBytes(MAX_FILE_SIZE)}.`, "error");
      return;
    }
    setFile(f);
    setErrors((prev) => ({ ...prev, body: undefined }));
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]);
  };

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyKey(key);
      setTimeout(() => setCopyKey(null), 1500);
    } catch (_) {
      showSnackbar("Não foi possível copiar.", "error");
    }
  };

  const codeSnippets = useMemo(() => {
    const url = endpointUrl;
    const statusUrl = `${backendUrl}/api/messages/status/${apiToken || "{token}"}/{jobId}`;
    const payload = buildRequestPayload();
    const payloadJson = JSON.stringify(payload, null, 2);

    const curl = `# Envio (síncrono — aguarda o worker terminar e devolve o resultado)
curl --location '${url}' \\
  --header 'Content-Type: application/json' \\
  --data '${JSON.stringify(payload)}'

# Opcional: envio assíncrono (devolve jobId imediato, processa em background)
#   adicione '?async=1' na URL e depois consulte o status:
# curl '${statusUrl}'

# Opcional: idempotência (evita duplicação em retry de cliente)
#   --header 'X-Idempotency-Key: sua-chave-unica'`;

    const javascript = `const response = await fetch("${url}", {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(${payloadJson})
});

const data = await response.json();
console.log(data);

// Opcional — modo assíncrono:
//   fetch("${url}?async=1", { ... })  → resposta { jobId, statusUrl }
//   depois: fetch(data.statusUrl) até status === "completed" | "failed"
//
// Opcional — idempotência:
//   headers["X-Idempotency-Key"] = "sua-chave-unica"`;

    const nodejs = `const axios = require("axios");

const { data } = await axios.post(
  "${url}",
  ${payloadJson},
  { headers: { "Content-Type": "application/json" } }
);

console.log(data);

// Opcional: passe header "X-Idempotency-Key" pra evitar duplicação em retry
// Opcional: adicione '?async=1' na URL pra modo assíncrono`;

    const python = `import requests

url = "${url}"
payload = ${payloadJson.replace(/: null/g, ": None").replace(/: true/g, ": True").replace(/: false/g, ": False")}

response = requests.post(url, json=payload)
print(response.json())

# Opcional: headers={"X-Idempotency-Key": "sua-chave"}  -> retry sem duplicar
# Opcional: url + "?async=1"                            -> resposta com jobId`;

    const php = `<?php
$url = "${url}";
$payload = ${payloadJson};

$ch = curl_init($url);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, ["Content-Type: application/json"]);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($payload));

$response = curl_exec($ch);
curl_close($ch);
echo $response;

// Opcional: adicione "X-Idempotency-Key: sua-chave" em CURLOPT_HTTPHEADER
// Opcional: $url . "?async=1" devolve jobId imediato (consulte /status/...)`;

    return [
      { label: "cURL", code: curl, language: "bash" },
      { label: "JavaScript", code: javascript, language: "javascript" },
      { label: "Node.js", code: nodejs, language: "javascript" },
      { label: "Python", code: python, language: "python" },
      { label: "PHP", code: php, language: "php" },
    ];
  }, [endpointUrl, buildRequestPayload, apiToken, backendUrl]);

  const activeHistory = history.find((h) => h.id === selectedHistoryId);
  const displayedResult = activeHistory ? activeHistory.result : response;
  const displayedPayload = activeHistory ? activeHistory.payload : buildRequestPayload();

  const renderHeaders = (headers) => {
    if (!headers) return null;
    const entries = Object.entries(headers);
    if (!entries.length) return <Typography color="text.secondary">Sem cabeçalhos.</Typography>;
    return (
      <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          {entries.map(([k, v]) => (
            <tr key={k}>
              <td
                style={{
                  padding: "6px 10px",
                  fontFamily: "ui-monospace, SFMono-Regular, monospace",
                  fontSize: 12,
                  fontWeight: 600,
                  verticalAlign: "top",
                  width: "30%",
                  borderBottom: "1px solid rgba(127,127,127,0.15)",
                }}
              >
                {k}
              </td>
              <td
                style={{
                  padding: "6px 10px",
                  fontFamily: "ui-monospace, SFMono-Regular, monospace",
                  fontSize: 12,
                  wordBreak: "break-all",
                  borderBottom: "1px solid rgba(127,127,127,0.15)",
                }}
              >
                {String(v)}
              </td>
            </tr>
          ))}
        </tbody>
      </Box>
    );
  };

  return (
    <Box className={classes.root}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box className={classes.header}>
          <Typography variant="h4" className={classes.headerTitle}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <SendIcon sx={{ color: "#fff", fontSize: 22 }} />
            </Box>
            API de Mensagens
          </Typography>
          <Typography variant="body2" className={classes.headerSubtitle}>
            Envie mensagens de WhatsApp, anexe arquivos e crie tickets via REST. Selecione uma
            conexão para começar.
          </Typography>
        </Box>

        {/* Connection error */}
        {connectionsError && (
          <Alert
            severity="error"
            sx={{ mb: 2, borderRadius: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={loadConnections}
              >
                Tentar novamente
              </Button>
            }
          >
            <AlertTitle>Falha ao carregar conexões</AlertTitle>
            {connectionsError}
          </Alert>
        )}

        {/* Endpoint bar */}
        <Box className={classes.endpointBar} sx={{ mb: 2 }}>
          <Box className={classes.methodBadge}>POST</Box>
          <Box className={classes.endpointUrl}>
            <span>{backendUrl}/api/messages/send/</span>
            <span className={classes.endpointToken}>{apiToken || "{token}"}</span>
          </Box>
          <Button
            className={classes.sendButton}
            variant="contained"
            color="primary"
            size="small"
            onClick={handleSend}
            disabled={sending || !connectionId}
            startIcon={sending ? <CircularProgress size={14} color="inherit" /> : <SendIcon fontSize="small" />}
          >
            {sending ? "Enviando" : "Enviar"}
          </Button>
        </Box>

        {/* Painel de proteção anti-ban: jitter + cotas */}
        <QuotaStrip rateInfo={rateInfo} classes={classes} />

        <Grid container spacing={2}>
          {/* LEFT: Request builder */}
          <Grid item xs={12} lg={7}>
            <Paper className={classes.panel} elevation={0}>
              <Box className={classes.panelHeader}>
                <Typography className={classes.panelTitle}>Requisição</Typography>
                <Box className={classes.connectionStatus}>
                  <span
                    className={classes.connectionDot}
                    style={{ backgroundColor: connectionId ? "#10b981" : "#9ca3af" }}
                  />
                  {loadingConnections
                    ? "Carregando conexões…"
                    : `${connections.length} ${connections.length === 1 ? "conexão" : "conexões"}`}
                </Box>
              </Box>

              <Tabs
                value={requestTab}
                onChange={(_, v) => setRequestTab(v)}
                variant="scrollable"
                sx={{ px: 1.5, borderBottom: 1, borderColor: "divider", minHeight: 38 }}
                TabIndicatorProps={{ style: { height: 2 } }}
              >
                <Tab label="Conexão" sx={{ minHeight: 38, textTransform: "none", fontSize: 13, py: 0.5 }} />
                <Tab label="Body" sx={{ minHeight: 38, textTransform: "none", fontSize: 13, py: 0.5 }} />
                <Tab label="Ticket" sx={{ minHeight: 38, textTransform: "none", fontSize: 13, py: 0.5 }} />
                <Tab label="Anexo" sx={{ minHeight: 38, textTransform: "none", fontSize: 13, py: 0.5 }} />
                <Tab label="Código" sx={{ minHeight: 38, textTransform: "none", fontSize: 13, py: 0.5 }} />
              </Tabs>

              <Box className={classes.panelBody}>
                {loadingConnections && (
                  <LinearProgress sx={{ mb: 1.5, borderRadius: 1 }} />
                )}

                {/* TAB 0: Conexão */}
                {requestTab === 0 && (
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} md={5}>
                      <Typography className={classes.sectionLabel}>Conexão WhatsApp</Typography>
                      <FormControl
                        fullWidth
                        size="small"
                        error={!!errors.connection}
                        disabled={loadingConnections}
                      >
                        <InputLabel>Selecione uma conexão</InputLabel>
                        <Select
                          label="Selecione uma conexão"
                          value={connectionId}
                          MenuProps={SELECT_MENU_PROPS}
                          onChange={(e) => {
                            setConnectionId(e.target.value);
                            setErrors((p) => ({ ...p, connection: undefined }));
                          }}
                        >
                          {connections.length === 0 && !loadingConnections && (
                            <MenuItem disabled value="">
                              <em>Nenhuma conexão disponível</em>
                            </MenuItem>
                          )}
                          {connections.map((c) => (
                            <MenuItem key={c.id} value={c.id} dense>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                sx={{ width: "100%" }}
                              >
                                <span>{c.name}</span>
                                {Array.isArray(c.queues) && c.queues.length > 0 && (
                                  <Chip
                                    size="small"
                                    label={`${c.queues.length} fila${c.queues.length > 1 ? "s" : ""}`}
                                    variant="outlined"
                                    sx={{ height: 18, fontSize: 10, "& .MuiChip-label": { px: 0.75 } }}
                                  />
                                )}
                              </Stack>
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.connection && (
                          <FormHelperText>{errors.connection}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={7}>
                      <Typography className={classes.sectionLabel}>Token da API</Typography>
                      <TextField
                        fullWidth
                        size="small"
                        value={apiToken}
                        placeholder="Selecione uma conexão para ver o token"
                        type={showToken ? "text" : "password"}
                        InputProps={{
                          readOnly: true,
                          startAdornment: (
                            <InputAdornment position="start">
                              <KeyIcon fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: apiToken && (
                            <InputAdornment position="end">
                              <Tooltip title={showToken ? "Ocultar" : "Mostrar"}>
                                <IconButton size="small" onClick={() => setShowToken((s) => !s)}>
                                  {showToken ? (
                                    <VisibilityOffIcon fontSize="small" />
                                  ) : (
                                    <VisibilityIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={copyKey === "token" ? "Copiado!" : "Copiar"}>
                                <IconButton size="small" onClick={() => copy(apiToken, "token")}>
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </InputAdornment>
                          ),
                          sx: {
                            fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                            fontSize: 12.5,
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Alert
                        severity="info"
                        variant="outlined"
                        icon={<InfoOutlinedIcon fontSize="small" />}
                        sx={{ py: 0.25, borderRadius: 1.5, "& .MuiAlert-message": { fontSize: 12.5 } }}
                      >
                        O token é único da conexão e autentica a chamada de envio. Mantenha-o em segredo.
                      </Alert>
                    </Grid>
                  </Grid>
                )}

                {/* TAB 1: Body */}
                {requestTab === 1 && (
                  <>
                    <Typography className={classes.sectionLabel}>Destinatário</Typography>
                    <Grid container spacing={1.5} alignItems="center" className={classes.fieldRow}>
                      <Grid item xs={12} sm={8}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Número (com DDI)"
                          placeholder="5511999999999"
                          value={number}
                          onChange={(e) => {
                            setNumber(e.target.value);
                            setErrors((p) => ({ ...p, number: undefined }));
                          }}
                          error={!!errors.number}
                          helperText={errors.number || "Apenas dígitos. Inclua o código do país."}
                          FormHelperTextProps={{ sx: { fontSize: 11, mt: 0.5, ml: 0.5 } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <FormControlLabel
                          sx={{ ml: 0 }}
                          control={
                            <Checkbox
                              size="small"
                              checked={saveContact}
                              onChange={(e) => setSaveContact(e.target.checked)}
                            />
                          }
                          label={<Typography variant="body2">Salvar contato</Typography>}
                        />
                      </Grid>
                    </Grid>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 0.75,
                        flexWrap: "wrap",
                        gap: 1,
                      }}
                    >
                      <Typography className={classes.sectionLabel} sx={{ mb: 0 }}>
                        Mensagem
                      </Typography>
                      <Stack direction="row" spacing={0.5}>
                        {MESSAGE_TEMPLATES.map((t) => (
                          <Chip
                            key={t.name}
                            size="small"
                            label={t.name}
                            onClick={() => {
                              setBody(t.body);
                              setErrors((p) => ({ ...p, body: undefined }));
                            }}
                            variant="outlined"
                            clickable
                            sx={{ height: 22, fontSize: 11 }}
                          />
                        ))}
                      </Stack>
                    </Box>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      minRows={6}
                      maxRows={14}
                      placeholder="Digite a mensagem que será enviada..."
                      value={body}
                      onChange={(e) => {
                        setBody(e.target.value);
                        setErrors((p) => ({ ...p, body: undefined }));
                      }}
                      error={!!errors.body}
                      helperText={errors.body || `${body.length} caracteres`}
                      FormHelperTextProps={{ sx: { fontSize: 11, mt: 0.5, ml: 0.5 } }}
                      InputProps={{ sx: { fontSize: 13.5 } }}
                    />
                  </>
                )}

                {/* TAB 2: Ticket */}
                {requestTab === 2 && (
                  <>
                    <FormControlLabel
                      sx={{ ml: 0 }}
                      control={
                        <Checkbox
                          size="small"
                          checked={createTicket}
                          onChange={(e) => setCreateTicket(e.target.checked)}
                        />
                      }
                      label={<Typography variant="body2">Criar ticket automaticamente</Typography>}
                    />
                    <Divider sx={{ my: 1.5 }} />
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small" disabled={!createTicket}>
                          <InputLabel>Status</InputLabel>
                          <Select
                            label="Status"
                            value={ticketStatus}
                            MenuProps={SELECT_MENU_PROPS}
                            onChange={(e) => setTicketStatus(e.target.value)}
                          >
                            {TICKET_STATUSES.map((s) => (
                              <MenuItem key={s.value} value={s.value} dense>
                                {s.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Autocomplete
                          size="small"
                          fullWidth
                          disabled={!createTicket}
                          options={users}
                          getOptionLabel={(o) => (typeof o === "string" ? o : o?.name || "")}
                          isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                          value={users.find((u) => u.id === ticketUser) || null}
                          onChange={(_, val) => {
                            setTicketUser(val ? val.id : "");
                            setErrors((p) => ({ ...p, ticketUser: undefined }));
                          }}
                          noOptionsText="Nenhum atendente"
                          clearText="Limpar"
                          openText="Abrir"
                          closeText="Fechar"
                          ListboxProps={{
                            sx: {
                              maxHeight: 260,
                              "& .MuiAutocomplete-option": { fontSize: 13, minHeight: 32, py: 0.5 },
                            },
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              label="Atendente"
                              placeholder="Buscar..."
                              error={!!errors.ticketUser}
                              helperText={errors.ticketUser}
                              FormHelperTextProps={{ sx: { fontSize: 11, mt: 0.5, ml: 0.5 } }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <FormControl
                          fullWidth
                          size="small"
                          disabled={!createTicket || queues.length === 0}
                        >
                          <InputLabel>Fila</InputLabel>
                          <Select
                            label="Fila"
                            value={ticketQueue}
                            MenuProps={SELECT_MENU_PROPS}
                            onChange={(e) => setTicketQueue(e.target.value)}
                          >
                            <MenuItem value="" dense>
                              <em>Sem fila</em>
                            </MenuItem>
                            {queues.map((q) => (
                              <MenuItem key={q.id} value={q.id} dense>
                                {q.name}
                              </MenuItem>
                            ))}
                          </Select>
                          {createTicket && queues.length === 0 && (
                            <FormHelperText sx={{ fontSize: 11, mt: 0.5, ml: 0.5 }}>
                              Esta conexão não possui filas.
                            </FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    </Grid>
                    {createTicket && (ticketStatus === "open" || ticketStatus === "archived") && (
                      <Alert
                        severity="info"
                        variant="outlined"
                        icon={<InfoOutlinedIcon fontSize="small" />}
                        sx={{
                          mt: 1.5,
                          py: 0.25,
                          borderRadius: 1.5,
                          "& .MuiAlert-message": { fontSize: 12.5 },
                        }}
                      >
                        Status <strong>{ticketStatus}</strong> requer um atendente atribuído.
                      </Alert>
                    )}
                  </>
                )}

                {/* TAB 3: Anexo */}
                {requestTab === 3 && (
                  <>
                    {file ? (
                      <Box className={classes.filePill}>
                        <AttachFileIcon fontSize="small" color="primary" />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {file.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatBytes(file.size)} · {file.type || "tipo desconhecido"}
                          </Typography>
                        </Box>
                        <IconButton size="small" onClick={() => setFile(null)}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <Box
                        className={cx(classes.fileDrop, dragOver && classes.fileDropActive)}
                        onClick={() => fileInputRef.current?.click()}
                        onDragOver={(e) => {
                          e.preventDefault();
                          setDragOver(true);
                        }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={onDrop}
                      >
                        <CloudUploadIcon sx={{ fontSize: 30, color: "text.secondary", mb: 0.5 }} />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Clique ou arraste um arquivo
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Imagens, áudios, vídeos, PDFs e documentos · até {formatBytes(MAX_FILE_SIZE)}
                        </Typography>
                        <input
                          ref={fileInputRef}
                          type="file"
                          hidden
                          onChange={(e) => handleFile(e.target.files?.[0])}
                        />
                      </Box>
                    )}
                  </>
                )}

                {/* TAB 4: Código */}
                {requestTab === 4 && (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 1,
                        borderBottom: 1,
                        borderColor: "divider",
                      }}
                    >
                      <Tabs
                        className={classes.languageTabs}
                        value={codeLanguage}
                        onChange={(_, v) => setCodeLanguage(v)}
                        variant="scrollable"
                        TabIndicatorProps={{ style: { height: 2 } }}
                      >
                        {codeSnippets.map((s, i) => (
                          <Tab key={i} label={s.label} />
                        ))}
                      </Tabs>
                      <Button
                        size="small"
                        sx={{ ml: 1, mr: 0.5, fontSize: 12, textTransform: "none" }}
                        startIcon={<ContentCopyIcon fontSize="small" />}
                        onClick={() => copy(codeSnippets[codeLanguage].code, "code")}
                      >
                        {copyKey === "code" ? "Copiado!" : "Copiar"}
                      </Button>
                    </Box>
                    <Box className={classes.codeBlock}>
                      <SyntaxHighlighter
                        language={codeSnippets[codeLanguage].language}
                        style={vscDarkPlus}
                        customStyle={{ fontSize: 12.5, padding: 12 }}
                      >
                        {codeSnippets[codeLanguage].code}
                      </SyntaxHighlighter>
                    </Box>
                  </>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* RIGHT: Response + History */}
          <Grid item xs={12} lg={5}>
            <Stack spacing={2}>
              {/* Response panel */}
              <Paper className={classes.panel} elevation={0}>
                <Box className={classes.panelHeader}>
                  <Typography className={classes.panelTitle}>Resposta</Typography>
                  {activeHistory && (
                    <Chip
                      size="small"
                      label="Visualizando do histórico"
                      onDelete={() => setSelectedHistoryId(null)}
                      sx={{ height: 20, fontSize: 11 }}
                    />
                  )}
                </Box>

                {displayedResult ? (
                  <>
                    <Box className={classes.metaBar}>
                      <Chip
                        className={classes.statusChip}
                        size="small"
                        color={statusColor(displayedResult.status)}
                        icon={
                          displayedResult.ok ? (
                            <CheckCircleIcon style={{ fontSize: 14 }} />
                          ) : (
                            <ErrorOutlineIcon style={{ fontSize: 14 }} />
                          )
                        }
                        label={`${displayedResult.status || "—"} ${
                          statusText(displayedResult.status) || displayedResult.statusText || ""
                        }`.trim()}
                        sx={{ height: 22, fontSize: 11 }}
                      />
                      <Box className={classes.metaItem}>
                        Tempo: <span className={classes.metaValue}>{displayedResult.time}ms</span>
                      </Box>
                      <Box className={classes.metaItem}>
                        Tamanho:{" "}
                        <span className={classes.metaValue}>{formatBytes(displayedResult.size)}</span>
                      </Box>
                      {displayedResult.meta?.jobId && (
                        <Tooltip title="ID do job na fila (use no /status para consultar)">
                          <Chip
                            size="small"
                            variant="outlined"
                            icon={<TagIcon sx={{ fontSize: 12 }} />}
                            label={String(displayedResult.meta.jobId).slice(0, 20)}
                            sx={{ height: 22, fontSize: 11, fontFamily: 'ui-monospace, monospace' }}
                          />
                        </Tooltip>
                      )}
                    </Box>

                    {/* Avisos do worker (contato/ticket existentes, rate-limit etc.) */}
                    {(displayedResult.rateLimited ||
                      displayedResult.meta?.warnContact ||
                      displayedResult.meta?.warnTicket ||
                      (displayedResult.meta?.warnings || []).length > 0) && (
                      <Box
                        sx={{
                          px: 1.5,
                          py: 1,
                          borderBottom: 1,
                          borderColor: "divider",
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.75,
                        }}
                      >
                        {displayedResult.rateLimited && (
                          <Chip
                            size="small"
                            color="warning"
                            icon={<ShieldIcon sx={{ fontSize: 14 }} />}
                            label="Cota atingida"
                            sx={{ height: 22, fontSize: 11 }}
                          />
                        )}
                        {displayedResult.meta?.warnContact && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label="contato já existia"
                            sx={{ height: 22, fontSize: 11 }}
                          />
                        )}
                        {displayedResult.meta?.warnTicket && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label="ticket já existia"
                            sx={{ height: 22, fontSize: 11 }}
                          />
                        )}
                        {(displayedResult.meta?.warnings || []).map((w) => (
                          <Chip
                            key={w}
                            size="small"
                            variant="outlined"
                            label={w}
                            sx={{ height: 22, fontSize: 11 }}
                          />
                        ))}
                      </Box>
                    )}

                    <Tabs
                      value={responseTab}
                      onChange={(_, v) => setResponseTab(v)}
                      sx={{ px: 1.5, borderBottom: 1, borderColor: "divider", minHeight: 34 }}
                      TabIndicatorProps={{ style: { height: 2 } }}
                    >
                      <Tab label="Body" sx={{ minHeight: 34, textTransform: "none", fontSize: 12.5, py: 0.5 }} />
                      <Tab label="Headers" sx={{ minHeight: 34, textTransform: "none", fontSize: 12.5, py: 0.5 }} />
                      <Tab label="Request" sx={{ minHeight: 34, textTransform: "none", fontSize: 12.5, py: 0.5 }} />
                    </Tabs>

                    <Box sx={{ p: 1.25 }}>
                      {responseTab === 0 && (
                        <Box className={classes.codeBlock}>
                          <SyntaxHighlighter
                            language="json"
                            style={vscDarkPlus}
                            customStyle={{ fontSize: 12, padding: 12 }}
                          >
                            {JSON.stringify(displayedResult.data, null, 2)}
                          </SyntaxHighlighter>
                        </Box>
                      )}
                      {responseTab === 1 && (
                        <Box sx={{ p: 0.5, maxHeight: 360, overflowY: "auto" }}>
                          {renderHeaders(displayedResult.headers)}
                        </Box>
                      )}
                      {responseTab === 2 && (
                        <Box className={classes.codeBlock}>
                          <SyntaxHighlighter
                            language="json"
                            style={vscDarkPlus}
                            customStyle={{ fontSize: 12, padding: 12 }}
                          >
                            {JSON.stringify(displayedPayload, null, 2)}
                          </SyntaxHighlighter>
                        </Box>
                      )}
                    </Box>
                  </>
                ) : (
                  <Box className={classes.emptyState}>
                    <InfoOutlinedIcon sx={{ fontSize: 30, mb: 0.5, opacity: 0.5 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Nenhuma resposta ainda
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Configure e envie uma requisição para ver o resultado aqui.
                    </Typography>
                  </Box>
                )}
              </Paper>

              {/* History panel */}
              <Paper className={classes.panel} elevation={0}>
                <Box className={classes.panelHeader}>
                  <Box display="flex" alignItems="center" gap={0.75}>
                    <HistoryIcon fontSize="small" sx={{ fontSize: 16, color: "text.secondary" }} />
                    <Typography className={classes.panelTitle}>Histórico</Typography>
                    <Chip
                      size="small"
                      label={history.length}
                      sx={{ height: 18, fontSize: 10, ml: 0.25 }}
                    />
                  </Box>
                  {history.length > 0 && (
                    <Tooltip title="Limpar histórico">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setHistory([]);
                          setSelectedHistoryId(null);
                          localStorage.removeItem(STORAGE_KEY);
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                {history.length === 0 ? (
                  <Box className={classes.emptyState}>
                    <Typography variant="caption" color="text.secondary">
                      Suas últimas requisições aparecerão aqui.
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ maxHeight: 320, overflowY: "auto" }}>
                    {history.map((h) => (
                      <Box
                        key={h.id}
                        className={cx(
                          classes.historyItem,
                          selectedHistoryId === h.id && classes.historyItemActive
                        )}
                        onClick={() =>
                          setSelectedHistoryId(selectedHistoryId === h.id ? null : h.id)
                        }
                      >
                        <span className={classes.historyMethod}>POST</span>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {h.number || "(sem número)"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {h.connectionName} · {new Date(h.ts).toLocaleString()}
                          </Typography>
                        </Box>
                        <Chip
                          size="small"
                          color={statusColor(h.result.status)}
                          label={h.result.status || "ERR"}
                          sx={{ height: 20, fontWeight: 700, fontSize: 11 }}
                        />
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>
            </Stack>
          </Grid>
        </Grid>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MessagesAPI;
